import { useEffect } from 'react';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import {
  allowJourneyCheckpointWrites,
  markJourneyReloadForRecovery,
  readJourneyCheckpoint,
  writeJourneyCheckpoint,
  type JourneyCheckpoint,
} from './journeyCheckpoint.js';
import { setPaymentPending } from './paymentPendingGate.js';

export type PaymentPendingCheckpoint = Omit<JourneyCheckpoint, 'interrupted'>;

function markCheckpointInterrupted(
  tenantCode: string,
  checkpoint: PaymentPendingCheckpoint,
): void {
  // Goes through writeJourneyCheckpoint so clearJourneyCheckpoint suppress applies (G4).
  writeJourneyCheckpoint(tenantCode, {
    ...checkpoint,
    interrupted: true,
  });
}

function isPersistedPageHide(event: Event): boolean {
  return 'persisted' in event && (event as PageTransitionEvent).persisted === true;
}

/** Register a payment-pending scope for PWA update deferral (Appendix N / M-06). */
export function usePaymentPendingScope(
  active: boolean,
  checkpoint?: PaymentPendingCheckpoint,
): void {
  const tenantCode = useTenantCode();

  useEffect(() => {
    if (!active) {
      return undefined;
    }
    setPaymentPending(true);

    if (checkpoint !== undefined) {
      allowJourneyCheckpointWrites(tenantCode);
      const existing = readJourneyCheckpoint(tenantCode);
      writeJourneyCheckpoint(tenantCode, {
        ...checkpoint,
        interrupted: existing?.interrupted === true,
      });
    }

    const persistInterruptedOnly = (event: Event): void => {
      if (checkpoint === undefined) {
        return;
      }
      // bfcache pagehide (persisted) — leave checkpoint alone; pageshow may restore.
      // NEVER arm reload flag here: SPA history POP can also fire pagehide (MOB-05 / G7).
      if (isPersistedPageHide(event)) {
        return;
      }
      markCheckpointInterrupted(tenantCode, checkpoint);
    };

    const persistInterruptedForDocumentUnload = (): void => {
      if (checkpoint === undefined) {
        return;
      }
      // Reload flag only on real document unload (beforeunload). iOS may skip this;
      // recovery then relies on one-shot Navigation Timing type=reload + interrupted.
      markJourneyReloadForRecovery(tenantCode);
      markCheckpointInterrupted(tenantCode, checkpoint);
    };

    const unloadOptions: AddEventListenerOptions = { capture: true };
    window.addEventListener('pagehide', persistInterruptedOnly, unloadOptions);
    window.addEventListener('beforeunload', persistInterruptedForDocumentUnload, unloadOptions);

    return () => {
      window.removeEventListener('pagehide', persistInterruptedOnly, unloadOptions);
      window.removeEventListener('beforeunload', persistInterruptedForDocumentUnload, unloadOptions);
      setPaymentPending(false);
    };
  }, [active, checkpoint, tenantCode]);
}
